import {
  DataSubjectChangeHistoryDto,
  getValidPeriodDisplayValueWithFormValueAttached,
  Table,
  TextEllipsis,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import type { FilterOption } from '@atlas-frontend/sdk';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import translations from './DataSubjectHistoryTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-subject-history-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<DataSubjectChangeHistoryDto>();

export function DataSubjectHistoryTable({
  data,
  isLoading,
}: Readonly<{ data: DataSubjectChangeHistoryDto[]; isLoading?: boolean }>) {
  const selectOptions = useSystemClassifierSelectOptions([
    'DATA_SUBJECT_ATTRIBUTE',
  ])['DATA_SUBJECT_ATTRIBUTE'];

  const { t } = useTranslation();

  const cols = useMemo(
    () =>
      [
        columnHelper.accessor('classifierId', {
          header: t('column.classifierId'),
          cell: (props) => (
            <TextEllipsis>{props.row.original.classifierName}</TextEllipsis>
          ),
          filterFn: 'includesOption',
          meta: {
            width: '40ch',
          },
        }),
        columnHelper.accessor('value', {
          header: t('column.value'),
          cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
          filterFn: 'includesOption',
          meta: {
            minWidth: 150,
          },
        }),
        columnHelper.accessor(
          (rowData) =>
            getValidPeriodDisplayValueWithFormValueAttached(
              rowData.validPeriod
            ),
          {
            header: t('column.validPeriod'),
            cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
            filterFn: 'validPeriod',
            meta: {
              width: 180,
            },
          }
        ),
      ] as ColumnDef<DataSubjectChangeHistoryDto>[],

    [t]
  );

  const options = useMemo(
    () => ({
      classifierId:
        selectOptions.map(({ value, label }) => ({
          id: value,
          name: label,
        })) ?? [],
      value: uniqueAttributes(data),
    }),
    [data, selectOptions]
  );

  return (
    <Flex align="end" gap="middle" vertical>
      <Table
        columns={cols}
        data={data}
        isLoading={isLoading}
        name="subjectHistoryTable"
        options={options}
        scrollContainerSelector="#detail-layout-main-content"
        sticky
      />
    </Flex>
  );
}

function uniqueAttributes(content: DataSubjectChangeHistoryDto[]) {
  const values = new Set<string>();
  const options: FilterOption[] = [];

  content.forEach((history) => {
    const value = history.value ?? '';
    if (!values.has(value)) {
      values.add(value);
      options.push({ id: value, name: value });
    }
  });

  options.sort((a, b) => a.name.localeCompare(b.name));
  return options;
}
